/**
 * 입점사 알림 공용 유틸 (2026-09-04 R69·R70)
 *
 * 배경
 *  - 알림 목록은 공지/주문/클레임/상품승인 API를 조합해 화면에서 즉석으로 만든다.
 *    읽음 여부를 담을 저장소가 없어 '읽음'이 화면을 벗어나면 사라졌고(R69),
 *    상단 알림 아이콘 개수는 아예 상수 3이었다(R70).
 *  - 알림 원본이 여러 API에 흩어져 있어 서버에 알림 테이블을 두려면 범위가 커진다.
 *    우선 브라우저(localStorage)에 읽음 표시만 남기고, 목록 조립은 두 화면이 공유한다.
 *
 * 읽음 키는 화면 재진입 후에도 같아야 하므로 원본 식별자로 만든다.
 *   notice:<공지ID> / order:<주문번호> / claim:<주문번호> / approve:<상품ID>
 */

const STORE_PREFIX = 'seller_noti_read_'

function sellerKey() {
  try {
    const raw = sessionStorage.getItem('seller_info')
    if (raw) {
      const info = JSON.parse(raw)
      if (info && (info.id || info.loginId)) return String(info.id || info.loginId)
    }
  } catch (e) { /* noop */ }
  return 'default'
}

function storeName() { return STORE_PREFIX + sellerKey() }

/** 읽음 처리된 키 집합 */
export function readKeys() {
  try {
    const raw = localStorage.getItem(storeName())
    const arr = raw ? JSON.parse(raw) : []
    return new Set(Array.isArray(arr) ? arr : [])
  } catch (e) { return new Set() }
}

function saveKeys(set) {
  try {
    // 무한정 쌓이지 않도록 최근 500건만 유지
    localStorage.setItem(storeName(), JSON.stringify([...set].slice(-500)))
  } catch (e) { /* 저장 실패해도 화면 동작에는 지장 없음 */ }
}

/** 알림 1건 읽음 */
export function markRead(key) {
  if (!key) return
  const s = readKeys()
  s.add(key)
  saveKeys(s)
}

/** 여러 건 읽음 (모두 읽음) */
export function markAllRead(keys) {
  const s = readKeys()
  ;(keys || []).forEach(k => { if (k) s.add(k) })
  saveKeys(s)
}

function dateText(v) { return (v || '').toString().replace('T', ' ').slice(5, 16) }

/**
 * 알림 목록 조립. 실패한 API는 건너뛰고 나머지로 구성한다.
 * @param {object} axios 인스턴스
 * @returns {Promise<Array>} [{ key, type, title, text, time, read }]
 */
export async function buildNotifications(axios) {
  const items = []
  const done = readKeys()

  try {
    const res = await axios.get('/api/seller/notices')
    ;(Array.isArray(res.data) ? res.data : []).forEach(n => {
      items.push({ key: 'notice:' + n.id, type: 'notice', title: '본사 공지', text: n.title, time: dateText(n.createdAt) })
    })
  } catch (e) { /* noop */ }

  try {
    const res = await axios.get('/api/seller/orders')
    const list = Array.isArray(res.data) ? res.data : []
    list.filter(o => o.orderStatus === 'PENDING').slice(0, 10).forEach(o => {
      items.push({ key: 'order:' + o.orderNumber, type: 'order', title: '신규 주문', text: o.orderNumber + ' · ' + o.productName, time: dateText(o.paymentDate) })
    })
    list.filter(o => o.claimStatus && o.claimStatus !== 'NONE').slice(0, 10).forEach(o => {
      items.push({ key: 'claim:' + o.orderNumber, type: 'claim', title: '클레임', text: o.orderNumber + ' · ' + o.productName, time: dateText(o.paymentDate) })
    })
  } catch (e) { /* noop */ }

  try {
    const res = await axios.get('/api/seller/products')
    ;(Array.isArray(res.data) ? res.data : []).filter(p => p.approvalStatus === 'APPROVED').slice(0, 10).forEach(p => {
      items.push({ key: 'approve:' + p.id, type: 'approve', title: '상품 승인 완료', text: p.name + ' 판매 시작', time: '' })
    })
  } catch (e) { /* noop */ }

  // 같은 원본이 두 번 담기지 않도록 키 기준 중복 제거
  const seen = new Set()
  return items.filter(n => {
    if (seen.has(n.key)) return false
    seen.add(n.key)
    n.read = done.has(n.key)
    return true
  })
}

/** 상단 알림 아이콘용 안읽은 개수 */
export async function unreadCount(axios) {
  const list = await buildNotifications(axios)
  return list.filter(n => !n.read).length
}
